import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['container'];

  connect() {
    this.boundKeydown = this.#keydown.bind(this);
    this.boundKeyup = this.#keyup.bind(this);
    this.boundBlur = this.#hide.bind(this);
    document.addEventListener('keydown', this.boundKeydown);
    document.addEventListener('keyup', this.boundKeyup);
    window.addEventListener('blur', this.boundBlur);
  }

  trigger(event) {
    if (event.isComposing) return;
    event.preventDefault();
    this.#activate(event);
  }

  disconnect() {
    document.removeEventListener('keydown', this.boundKeydown);
    document.removeEventListener('keyup', this.boundKeyup);
    window.removeEventListener('blur', this.boundBlur);
  }

  #keydown(event) {
    if (event.defaultPrevented || event.isComposing) return;
    if (event.key === 'Control') {
      this.element.classList.add('keybind-is-active');
      return;
    }

    this.#activate(event);
  }

  #keyup(event) {
    if (event.key === 'Control' || !event.ctrlKey) {
      this.#hide();
    }
  }

  #hide() {
    this.element.classList.remove('keybind-is-active');
  }

  #activate(event) {
    const keybind = this.#candidateContainers(event).find((container) => {
      const focusSelector = container.dataset.keybindFocusSelector;
      return (!focusSelector || event.target.matches(focusSelector)) &&
        this.#isVisible(container) && this.#matches(container.dataset.keybindKeys, event);
    });

    if (!keybind) return;

    event.preventDefault();
    const activation = new CustomEvent('keybind:trigger', { bubbles: true, cancelable: true });
    if (!keybind.dispatchEvent(activation)) return;
    const trigger = keybind.closest('a, button, input[type="submit"]');

    if (trigger) {
      trigger.click();
      return;
    }

    keybind.closest('form')?.requestSubmit();
  }

  #matches(keys, event) {
    if (!keys) return false;

    const expected = keys.split('+').map((key) => key.toLowerCase());
    const pressed = [];

    if (event.ctrlKey) pressed.push('ctrl');
    if (event.shiftKey) pressed.push('shift');
    if (event.altKey) pressed.push('alt');
    if (event.metaKey) pressed.push('meta');

    const key = event.key.toLowerCase();
    if (!['control', 'shift', 'alt', 'meta'].includes(key)) pressed.push(this.#normalizeKey(key));

    return expected.map((key) => this.#normalizeKey(key)).sort().join('+') === pressed.sort().join('+');
  }

  #normalizeKey(key) {
    if (key === 'arrowleft') return 'left';
    if (key === 'arrowright') return 'right';
    if (key === 'escape') return 'esc';
    return key;
  }

  #candidateContainers(event) {
    const activeModal = this.element.closest('dialog.utmr[open]') || this.element.querySelector('dialog.utmr[open]');
    if (activeModal) return this.containerTargets.filter((container) => activeModal.contains(container));

    const form = event.target.closest('form');
    if (form) return this.containerTargets.filter((container) => {
      return form.contains(container) || container.closest('button, input[type="submit"]')?.form === form;
    });

    return this.containerTargets;
  }

  #isVisible(element) {
    return element.offsetParent !== null && !element.closest('[inert]');
  }
}
